import React, { cloneElement, createContext, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { closeModal } from '@guryou/html-components';

import { SystemDialog } from './SystemLayout';

// Deal-design-system-styled drop-in for @guryou/html-components' <Modal> — same
// redux-registry "open by id" mechanism (dispatch(openModal(id, options, title))
// elsewhere still opens it; this reads state.modal.data to find it and clones `options`
// onto `children`, exactly like the original), so existing dispatch(openModal(...))/
// dispatch(closeModal(...)) call sites don't change. Only the chrome is SystemDialog
// instead of the legacy hand-styled MUI Dialog (hardcoded #E6616A border, #1f2937/#6b7280
// text, gradient header — none of it dealTokens).
//
// Migrate <Modal id="..."> call sites to this one at a time, not as a global swap —
// @guryou/html-components' Modal is untouched and keeps working for every id not yet
// migrated. `width` is a literal pixel value (SystemDialog's own convention), not a MUI
// breakpoint string like the original's `maxWidth="lg"/"md"` — pick a value that fits the
// modal's actual content instead of translating the old breakpoint name.
// Lets the content of an open SystemModal ask for another width while it shows something
// narrower or wider than the modal's default (e.g. a single field being edited inside a wide
// details modal). Pass `null` to go back to the default; leaving the content resets it too.
const ModalWidthContext = createContext(() => {});

export const useModalWidth = width => {
  const setWidth = useContext(ModalWidthContext);
  useEffect(() => {
    setWidth(width || null);
    return () => setWidth(null);
  }, [setWidth, width]);
};

// The open dialog; its width override lives here, so it is dropped when the modal closes.
const OpenSystemModal = ({ id, modalData, title, description, width, height, footer, bodyStyle, children }) => {
  const dispatch = useDispatch();
  const [contentWidth, setContentWidth] = useState(null);

  // `options.width` is this component's equivalent of the original's per-call
  // `options.maxWidth` escape hatch, for the rare dispatch(openModal(...)) that needs a
  // different size than the modal's own default.
  const { width: oWidth, ...childProps } = modalData.options || {};
  const onClose = () => dispatch(closeModal(id));

  return (
    <ModalWidthContext.Provider value={setContentWidth}>
      <SystemDialog
        open
        onClose={onClose}
        title={modalData.title || title}
        description={description}
        width={contentWidth || oWidth || width}
        height={height}
        footer={footer}
        bodyStyle={bodyStyle}
      >
        {cloneElement(children, childProps)}
      </SystemDialog>
    </ModalWidthContext.Provider>
  );
};

export const SystemModal = ({ id, width = 720, ...props }) => {
  const { data } = useSelector(state => state.modal);
  const modalData = data?.find(modal => modal.id === id);

  if (!modalData) {
    return null;
  }

  return <OpenSystemModal key={id} id={id} modalData={modalData} width={width} {...props} />;
};

export default SystemModal;
